/**
 * FieldModal — create/edit modal for custom formula fields.
 *
 * Features:
 *   - Name input
 *   - Formula textarea with event name hints
 *   - Live preview: parsed events list + validation status
 *   - Per-event parameter filters with property picker (fetched per event)
 *   - Save / Update / Cancel buttons
 */
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { X, AlertCircle, CheckCircle2, Plus, Filter, ChevronDown, ChevronRight, Trash2, Search, Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { api } from '@/services/api';
import { endpoints } from '@/services/endpoints';
import { extractEvents, validateFormula } from '@/lib/formula';
import { FormulaInput } from './FormulaInput';
import { FILTER_OPERATORS } from '../product/product.constants';

// ─── Types ──────────────────────────────────────────────────────────────────

interface EventFilterCondition {
  field: string;
  operator: string;
  value: string;
}

interface CustomField {
  id: string;
  name: string;
  formula: string;
  events: string[];
  eventFilters?: Record<string, EventFilterCondition[]>;
  createdAt: string;
  updatedAt: string;
}

interface FieldModalProps {
  editing?: CustomField;
  onClose: () => void;
  onSave: (field: CustomField) => void;
  /** Available event names for the formula @ picker */
  availableEvents?: string[];
  /** Current table — needed to fetch per-event properties */
  tableName?: string;
  /** Current start date */
  startDate?: string;
  /** Current end date */
  endDate?: string;
  /** Section scope for Firestore field storage (default: 'product') */
  sectionId?: string;
}

// ─── Property Picker ────────────────────────────────────────────────────────

/**
 * Searchable dropdown for selecting an event parameter.
 * Rendered via portal to escape parent overflow clipping.
 * Dynamically positions below (or above if no space) the trigger button.
 */
function PropertyPicker({
  value,
  properties,
  isLoading,
  onChange,
}: {
  value: string;
  properties: string[];
  isLoading: boolean;
  onChange: (property: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, flip: false });

  const filtered = useMemo(() => {
    if (!search) return properties;
    const q = search.toLowerCase();
    return properties.filter((p) => p.toLowerCase().includes(q));
  }, [properties, search]);

  // Position dropdown relative to trigger via portal
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const update = () => {
      const rect = triggerRef.current!.getBoundingClientRect();
      const dropdownHeight = 240; // estimated max height
      const spaceBelow = window.innerHeight - rect.bottom;
      const flip = spaceBelow < dropdownHeight && rect.top > spaceBelow;
      setPos({
        top: flip ? rect.top - 4 : rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 240),
        flip,
      });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const close = () => { setOpen(false); setSearch(''); };

  return (
    <div className="flex-1 min-w-0">
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          'w-full h-7 rounded border bg-white px-2 text-xs text-left',
          'flex items-center justify-between gap-1',
          'transition-colors duration-150 cursor-pointer',
          'focus:outline-none focus:ring-1 focus:ring-brand-400 focus:border-brand-400',
          open ? 'border-brand-400 ring-1 ring-brand-400' : 'border-slate-200 hover:border-slate-300',
        )}
      >
        <span className={cn('truncate font-mono', !value && 'text-slate-400')}>
          {value || 'Select parameter...'}
        </span>
        {isLoading ? (
          <Loader2 className="w-3 h-3 text-slate-400 shrink-0 animate-spin" />
        ) : (
          <ChevronDown className={cn(
            'w-3 h-3 text-slate-400 shrink-0 transition-transform duration-150',
            open && 'rotate-180',
          )} />
        )}
      </button>

      {/* Dropdown (portal) */}
      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={close} />
          <div
            ref={dropdownRef}
            className="fixed z-[9999] bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden"
            style={{
              top: pos.flip ? undefined : pos.top,
              bottom: pos.flip ? window.innerHeight - pos.top : undefined,
              left: pos.left,
              width: pos.width,
              maxWidth: 'min(360px, 90vw)',
            }}
          >
            {/* Search */}
            <div className="p-1.5 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search parameters..."
                  autoFocus
                  className="w-full pl-7 pr-2 py-1 text-xs rounded border border-slate-200 bg-slate-50 focus:outline-none focus:ring-1 focus:ring-brand-400 focus:border-brand-400 focus:bg-white transition-colors"
                />
              </div>
            </div>

            {/* List */}
            <div className="max-h-44 overflow-y-auto py-0.5">
              {isLoading ? (
                <div className="px-3 py-4 text-center text-xs text-slate-400 flex items-center justify-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Loading parameters...
                </div>
              ) : filtered.length === 0 ? (
                <div className="px-3 py-4 text-center text-xs text-slate-400">
                  {properties.length === 0 ? 'No parameters found' : 'No match'}
                </div>
              ) : (
                filtered.map((prop) => (
                  <button
                    key={prop}
                    type="button"
                    onClick={() => {
                      onChange(prop);
                      close();
                    }}
                    className={cn(
                      'w-full px-3 py-1.5 text-left text-xs font-mono transition-colors',
                      prop === value
                        ? 'bg-brand-50 text-brand-700 font-medium'
                        : 'text-slate-700 hover:bg-slate-50',
                    )}
                  >
                    {prop}
                  </button>
                ))
              )}
            </div>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}

// ─── Per-event Filter Row ───────────────────────────────────────────────────

function EventFilterRow({
  filter,
  properties,
  isLoadingProps,
  onChange,
  onRemove,
}: {
  filter: EventFilterCondition;
  properties: string[];
  isLoadingProps: boolean;
  onChange: (patch: Partial<EventFilterCondition>) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      {/* Property picker */}
      <PropertyPicker
        value={filter.field}
        properties={properties}
        isLoading={isLoadingProps}
        onChange={(field) => onChange({ field })}
      />

      {/* Operator */}
      <select
        value={filter.operator}
        onChange={(e) => onChange({ operator: e.target.value })}
        className={cn(
          'w-24 shrink-0 h-7 px-1.5 rounded border border-slate-200 bg-white text-xs',
          'focus:outline-none focus:ring-1 focus:ring-brand-400 focus:border-brand-400',
        )}
      >
        {FILTER_OPERATORS.map((op) => (
          <option key={op.value} value={op.value}>{op.label}</option>
        ))}
      </select>

      {/* Value */}
      <input
        type="text"
        value={filter.value}
        onChange={(e) => onChange({ value: e.target.value })}
        placeholder="value"
        className={cn(
          'flex-1 min-w-0 h-7 px-2 rounded border border-slate-200 bg-white text-xs',
          'focus:outline-none focus:ring-1 focus:ring-brand-400 focus:border-brand-400',
          'placeholder:text-slate-400',
        )}
      />

      {/* Remove */}
      <button
        type="button"
        onClick={onRemove}
        className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0"
      >
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}

// ─── Per-event Filter Section ───────────────────────────────────────────────

function EventFilterSection({
  eventName,
  filters,
  properties,
  isLoadingProps,
  onChange,
}: {
  eventName: string;
  filters: EventFilterCondition[];
  properties: string[];
  isLoadingProps: boolean;
  onChange: (filters: EventFilterCondition[]) => void;
}) {
  const [expanded, setExpanded] = useState(filters.length > 0);

  const addFilter = () => {
    onChange([...filters, { field: '', operator: '=', value: '' }]);
    setExpanded(true);
  };

  const updateFilter = (idx: number, patch: Partial<EventFilterCondition>) => {
    const next = filters.map((f, i) => (i === idx ? { ...f, ...patch } : f));
    onChange(next);
  };

  const removeFilter = (idx: number) => {
    onChange(filters.filter((_, i) => i !== idx));
  };

  return (
    <div className="rounded-lg border border-slate-150 bg-slate-50/50 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 transition-colors"
        >
          {expanded ? (
            <ChevronDown className="w-3 h-3 text-slate-400" />
          ) : (
            <ChevronRight className="w-3 h-3 text-slate-400" />
          )}
          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-indigo-100 text-indigo-700 text-xs font-mono">
            {eventName}
          </span>
          {filters.length > 0 && (
            <span className="flex items-center gap-0.5 text-brand-600">
              <Filter className="w-3 h-3" />
              <span className="text-2xs font-semibold">{filters.length}</span>
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={addFilter}
          className="flex items-center gap-1 px-2 py-1 rounded text-2xs font-medium text-brand-600 hover:bg-brand-50 transition-colors"
        >
          <Plus className="w-3 h-3" />
          Filter
        </button>
      </div>

      {expanded && filters.length > 0 && (
        <div className="px-3 pb-2.5 space-y-1.5">
          {filters.map((filter, idx) => (
            <EventFilterRow
              key={idx}
              filter={filter}
              properties={properties}
              isLoadingProps={isLoadingProps}
              onChange={(patch) => updateFilter(idx, patch)}
              onRemove={() => removeFilter(idx)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Modal ─────────────────────────────────────────────────────────────

export function FieldModal({
  editing,
  onClose,
  onSave,
  availableEvents = [],
  tableName = '',
  startDate = '',
  endDate = '',
  sectionId = 'product',
}: FieldModalProps) {
  const [name, setName] = useState(editing?.name || '');
  const [formula, setFormula] = useState(editing?.formula || '');
  const [eventFilters, setEventFilters] = useState<Record<string, EventFilterCondition[]>>(
    editing?.eventFilters || {},
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isEditing = !!editing;

  // Live validation
  const validation = useMemo(() => {
    if (!formula.trim()) return { valid: false, error: 'Formula is empty' };
    return validateFormula(formula);
  }, [formula]);

  // Extract events from formula
  const parsedEvents = useMemo(() => {
    if (!formula.trim()) return [];
    return extractEvents(formula);
  }, [formula]);

  // Fetch event properties for each individual formula event.
  // Uses a single query per event name, cached via react-query.
  // For marketing, tableName is empty so the query won't fire —
  // property pickers will be empty but the filter UI is still available.
  const perEventPropsQueries = parsedEvents;
  const propsQueryResults = usePerEventProperties(perEventPropsQueries, tableName, startDate, endDate);

  // Clean up eventFilters when events change (remove filters for events no longer in formula)
  useEffect(() => {
    const eventSet = new Set(parsedEvents);
    setEventFilters((prev) => {
      const next: Record<string, EventFilterCondition[]> = {};
      for (const [event, filters] of Object.entries(prev)) {
        if (eventSet.has(event)) {
          next[event] = filters;
        }
      }
      if (Object.keys(next).length !== Object.keys(prev).length) return next;
      return prev;
    });
  }, [parsedEvents]);

  // Can submit?
  const canSubmit = name.trim().length > 0 && validation.valid && !saving;

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  /** Strip empty filter conditions before saving */
  function cleanEventFilters(raw: Record<string, EventFilterCondition[]>): Record<string, EventFilterCondition[]> | undefined {
    const cleaned: Record<string, EventFilterCondition[]> = {};
    for (const [event, filters] of Object.entries(raw)) {
      const valid = filters.filter((f) => f.field && f.value);
      if (valid.length > 0) {
        cleaned[event] = valid;
      }
    }
    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
  }

  const handleSave = async () => {
    if (!canSubmit) return;

    setSaving(true);
    setError('');

    try {
      let field: CustomField;
      const cleanedFilters = cleanEventFilters(eventFilters);

      if (isEditing) {
        field = await api.patch<CustomField>(endpoints.fields.update(sectionId, editing.id), {
          name: name.trim(),
          formula: formula.trim(),
          eventFilters: cleanedFilters ?? null,
        });
      } else {
        field = await api.post<CustomField>(endpoints.fields.create(sectionId), {
          name: name.trim(),
          formula: formula.trim(),
          eventFilters: cleanedFilters ?? undefined,
        });
      }

      onSave(field);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save field');
    } finally {
      setSaving(false);
    }
  };

  const handleEventFiltersChange = (event: string, filters: EventFilterCondition[]) => {
    setEventFilters((prev) => {
      const next = { ...prev };
      if (filters.length === 0) {
        delete next[event];
      } else {
        next[event] = filters;
      }
      return next;
    });
  };

  // Count total per-event filters
  const totalEventFilters = Object.values(eventFilters).reduce((sum, f) => sum + f.length, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 overflow-visible max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <h2 className="text-lg font-semibold text-slate-900">
            {isEditing ? 'Edit Field' : 'Create Field'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body (scrollable) */}
        <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1 min-h-0">
          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Field Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., other_purchase"
              className={cn(
                'w-full h-9 rounded-lg border bg-white px-3 text-sm text-slate-900',
                'transition-colors duration-150',
                'focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400',
                'border-slate-200 hover:border-slate-300',
                'placeholder:text-slate-400',
              )}
              autoFocus
            />
          </div>

          {/* Formula */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Formula
            </label>
            <FormulaInput
              value={formula}
              onChange={setFormula}
              events={availableEvents}
              placeholder="Type @ to insert events, e.g. @event1 - (@event2 + @event3)"
            />
          </div>

          {/* Validation Status */}
          {formula.trim() && (
            <div
              className={cn(
                'flex items-start gap-2 px-3 py-2 rounded-lg text-xs',
                validation.valid
                  ? 'bg-emerald-50 text-emerald-700'
                  : validation.error === 'Keep typing…'
                    ? 'bg-amber-50 text-amber-600'
                    : 'bg-red-50 text-red-700',
              )}
            >
              {validation.valid ? (
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-px" />
              ) : (
                <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
              )}
              <div>
                {validation.valid ? (
                  <span>Valid formula</span>
                ) : (
                  <span>{validation.error}</span>
                )}
              </div>
            </div>
          )}

          {/* Per-Event Filters */}
          {parsedEvents.length > 0 && (
            <div>
              <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-2">
                <Filter className="w-3 h-3" />
                Event Filters
                {totalEventFilters > 0 && (
                  <span className="bg-brand-50 text-brand-600 px-1.5 py-0.5 rounded text-2xs font-semibold normal-case tracking-normal">
                    {totalEventFilters}
                  </span>
                )}
              </label>
              <div className="space-y-2">
                {parsedEvents.map((event) => {
                  const propsResult = propsQueryResults[event];
                  return (
                    <EventFilterSection
                      key={event}
                      eventName={event}
                      filters={eventFilters[event] || []}
                      properties={propsResult?.data || []}
                      isLoadingProps={propsResult?.isLoading ?? false}
                      onChange={(filters) => handleEventFiltersChange(event, filters)}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="px-3 py-2 rounded-lg bg-red-50 text-red-700 text-xs">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50/50 shrink-0">
          <button
            onClick={onClose}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium',
              'text-slate-700 hover:bg-slate-100 transition-colors',
            )}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSubmit}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium',
              'bg-brand-600 text-white hover:bg-brand-700 transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            {saving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Per-event Properties Hook ──────────────────────────────────────────────

/**
 * Fetches event properties for each event individually.
 * Uses react-query for caching, so repeated events don't re-fetch.
 * Returns a map of event_name → { data: string[], isLoading: boolean }.
 */
function usePerEventProperties(
  eventNames: string[],
  tableName: string,
  startDate: string,
  endDate: string,
): Record<string, { data: string[]; isLoading: boolean }> {
  // We use a single bulk query with all formula events combined.
  // The properties API already returns the intersection, but for per-event
  // use we call once per event to get that event's specific parameters.
  const results: Record<string, { data: string[]; isLoading: boolean }> = {};

  // Fetch each event's properties individually.
  // We call useQuery in a stable order (sorted event names) via a single combined query.
  const stableKey = eventNames.slice().sort().join(',');

  const { data, isLoading } = useQuery<Record<string, string[]>>({
    queryKey: ['per-event-properties', tableName, stableKey, startDate, endDate],
    queryFn: async () => {
      if (!tableName || eventNames.length === 0) return {};
      // Fetch properties per event in parallel
      const entries = await Promise.all(
        eventNames.map(async (event) => {
          try {
            const result = await api.post<{ properties: string[] }>(
              endpoints.analytics.properties(),
              { tableName, eventNames: [event], startDate, endDate },
            );
            return [event, result.properties || []] as const;
          } catch {
            return [event, []] as const;
          }
        }),
      );
      return Object.fromEntries(entries);
    },
    enabled: !!tableName && eventNames.length > 0 && !!startDate && !!endDate,
    staleTime: 60_000,
  });

  for (const event of eventNames) {
    results[event] = {
      data: data?.[event] || [],
      isLoading,
    };
  }

  return results;
}
